import { Plain } from './plain';

export namespace AiBudgetApi {
    export const CREDIT_WARNING_THRESHOLD = 0.8;

    // ~2 days runway at heavy usage — enough to buy another pack before depletion.
    export const EXTRA_WARNING_THRESHOLD = 500_000;

    // null = business is legacy (no budget row) or not on a paid plan
    export type Snapshot = {
        totalCredits: number;
        creditsUsed: number;
        rechargeBalance: number;
        cycleEndsAt: Plain.SimpleDate;
    };

    // approximateConversations follows the catalogue's 28k-credits-per-client-day rule.
    export type CreditPack = { credits: number; priceInCents: number; approximateConversations: number };

    export const DEFAULT_PACK: CreditPack = { credits: 3_500_000, priceInCents: 2800, approximateConversations: 120 };

    export function creditsPriceInCents(credits: number): number {
        return Math.round((credits / DEFAULT_PACK.credits) * DEFAULT_PACK.priceInCents);
    }

    export type PurchasePackResponse = { invoiceId: string; paymentUrl: string };

    export type PackHistoryEntry = {
        // Stable across webhook retries — it's the external provider's invoice id.
        id: string;
        credits: number;
        priceInCents: number;
        creditedAt: Plain.SimpleDate;
        // Charged to the stored card by auto-recharge rather than bought by the owner.
        automatic: boolean;
    };

    // Auto-recharge charges one DEFAULT_PACK to the stored card when the total
    // remaining credits (plan remainder + extras) drop below EXTRA_WARNING_THRESHOLD,
    // with no ceiling on how often that happens.
    // The card the pack is charged to is `subscription.creditCard`. Presence of this
    // object is the feature gate: it is omitted for a business that is not metered.
    export type AutoRechargeSettings = {
        enabled: boolean;
    };

    export type PackHistoryResponse = {
        month: Plain.Month;
        entries: PackHistoryEntry[];
    };

    // Mirrors the server-side SQL predicate.
    export function isSuspended(params: {
        totalCredits: number;
        creditsUsed: number;
        rechargeBalance: number;
    }): boolean {
        return params.creditsUsed >= params.totalCredits && params.rechargeBalance === 0;
    }

    // Mirrors the server-side SQL the crossing is computed from: the plan remainder
    // never goes negative, and the extra bucket sits on top. This is the number the
    // auto-recharge threshold is compared against.
    export function remainingCredits(params: {
        totalCredits: number;
        creditsUsed: number;
        rechargeBalance: number;
    }): number {
        return Math.max(params.totalCredits - params.creditsUsed, 0) + params.rechargeBalance;
    }

    // Reconstructs the peak extra bucket without a dedicated column: current balance
    // plus whatever was already consumed past the monthly cap.
    export function extraBucketSize(params: {
        totalCredits: number;
        creditsUsed: number;
        rechargeBalance: number;
    }): number {
        return params.rechargeBalance + Math.max(0, params.creditsUsed - params.totalCredits);
    }

    export const PACK_ERROR_ASAAS_CUSTOMER_NOT_FOUND = 'asaas_customer_not_found';
    export const PACK_ERROR_AI_BUDGET_NOT_ACTIVE = 'ai_budget_not_active';
    export const PACK_ERROR_NOT_ON_PAID_PLAN = 'not_on_paid_plan';

    export const AUTO_RECHARGE_ERROR_NO_CREDIT_CARD = 'no_credit_card';
    // Turning the setting on below EXTRA_WARNING_THRESHOLD charges a pack straight
    // away, so the server refuses until the caller confirms the owner agreed.
    export const AUTO_RECHARGE_ERROR_WOULD_CHARGE_NOW = 'would_charge_now';
}
